import { useEffect, useState, useSyncExternalStore } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { goToLoginAndReturn } from "@/auth/returnTo";
import { ensureSavedLoaded, isSaved, subscribeSaved, toggleSaved } from "@/api/savedStore";
import type { Listing } from "@/api/listings";
import type { MarketData } from "@/api/markets";
import { locationLabel } from "@/lib/format";
import { confirmedText } from "@/lib/confirmed";
import { useMarketLink } from "./links";
import { useMarket } from "./MarketProvider";
import { useV3 } from "./useV3";
import { marketPrice } from "./money";
import { Play } from "./Icons";

/* The listing card, from the design's listing-card component.
 *
 * FOUR BY THREE. The media box is `aspect-ratio:4/3` in the design's own CSS,
 * which is HOME-1 amendment 3 already satisfied -- nothing here sets it.
 *
 * NO PHOTO, NO IMG. A listing without a picture renders the media box empty
 * rather than a stand-in: the design gives the box its own background for
 * exactly this, and a generic house photo on a listing that has none is a
 * picture of a property nobody is selling.
 *
 * THE PRICE IS THE MARKET'S. Never composed from a symbol and a guess at
 * which side it goes -- see money.ts.
 */
export function ListingCardV3(
  { listing, geo, saveable }: {
    listing: Listing; geo?: MarketData | null;
    /** The heart. The design draws it on results cards, not on the home rail. */
    saveable?: boolean;
  },
) {
  const { s, locale, ar } = useV3();
  const { market } = useMarket();
  const link = useMarketLink();
  const [photoFailed, setPhotoFailed] = useState(false);

  const rent = listing.listingType === "Rent";
  /* THREE ANSWERS, NOT TWO. `listing_type` is "Sale", "Rent", or something
     this build has not met. The third used to be drawn as «للبيع» -- anything
     that was not Rent was called a sale -- which is a badge stating a fact the
     row does not. An unknown purpose now draws no badge. */
  const purpose = rent ? s.listing.forRent
    : listing.listingType === "Sale" ? s.listing.forSale : null;
  const price = marketPrice(listing.price, market, locale, s,
    { perMonth: rent, currency: listing.currency });
  /* THE AREA LABEL COMES FROM THE TABLES, not from listings.district_text,
     which is whatever the lister typed and is English on most rows -- "Nasr
     City, Cairo" on an Arabic page. name_ar / name_en are the same place
     written by us. A row the tables do not know falls back to the lister's
     text rather than vanishing. */
  const name = (r: { nameAr: string; nameEn: string } | undefined, fallback: string | null) =>
    r ? (ar ? r.nameAr || r.nameEn : r.nameEn || r.nameAr) : fallback;
  const district = name(
    geo?.districts.find((d) => d.id === listing.districtId), listing.districtText);
  const city = name(geo?.cities.find((c) => c.id === listing.cityId), listing.cityText);
  const where = locationLabel(district, city, locale);
  const confirmed = confirmedText(listing.availabilityConfirmedAt, s);
  const href = link(`/listing/${encodeURIComponent(listing.id)}`);

  return (
    <article data-component="listing-card" className="sq-listing-card">
      {/* THE WHOLE CARD IS ONE LINK, and the title carries it.
          The design's card is an <article> with the link on the MEDIA and none
          on the body, so clicking the title or the price -- where a reader's
          eye and cursor actually go -- did nothing. Its own CSS gives
          `.sq-listing-card` `text-decoration:none;color:inherit`, which means
          nothing on an <article>: the card was meant to be a link.

          Done with the stretched-link pattern rather than by wrapping
          everything: ONE link, named by the title, covering the whole card
          through a `::after` in v3-app.css. Two links to one listing would
          read as two listings to a screen reader. */}
      <div className="sq-listing-card__media">
        {/* A STORED URL THAT NO LONGER RESOLVES falls back to the empty box
            rather than a broken-image glyph. The legacy card had this and v3
            lost it: `photo_url` is a plain string in the row, so nothing stops
            it outliving the object it points at, and the failure a visitor saw
            was a torn-page icon in a card that otherwise looked fine. The
            empty box is what a listing with no photograph shows, which is the
            true statement in both cases. */}
        {listing.photoUrl && !photoFailed && (
          <img src={listing.photoUrl} alt="" loading="lazy" decoding="async"
            onError={() => setPhotoFailed(true)} />
        )}
        {/* EVERY BADGE NAMES THE COLUMN IT READS, in `data-field`. A badge
            that cannot name one is decoration, and decoration that looks like
            a fact is the thing HONEST SIGNALS forbids -- the gate fails any
            badge on a card without the attribute, and any badge whose row
            does not say what the badge says.

            `hasVideo` is derived from `video_url` in toListing. It used to
            mirror the `has_video` column, which said "yes" on four listings
            that had no video. */}
        {listing.hasVideo && (
          <span className="sq-badge sq-badge--dark sq-listing-card__video"
            data-badge="video" data-field="video_url">
            <Play />{s.listing.video}
          </span>
        )}
        {/* The purpose badge is only drawn when the row says which it is.
            "For sale" on a listing whose type is missing is a claim. */}
        {purpose && (
          <span className="sq-badge sq-badge--light sq-listing-card__purpose"
            data-badge="purpose" data-field="listing_type">{purpose}</span>
        )}
        {saveable && <SaveHeart id={listing.id} />}
      </div>
      <div className="sq-listing-card__body">
        {/* An empty price slot rather than a dash: a dash in a price reads as
            "free" to enough people to matter. */}
        {price && <div className="sq-listing-card__price">{price}</div>}
        <h3 className="sq-listing-card__title">
          <Link to={href} className="sq-stretch">{listing.title}</Link>
        </h3>
        {where && <div className="sq-listing-card__area">{where}</div>}
        {confirmed && (
          <div className="sq-listing-card__area" data-signal="confirmed" data-field="availability_confirmed_at">{confirmed}</div>
        )}
        <div className="sq-listing-card__facts">
          {listing.beds != null && <span><span>{listing.beds}</span> {s.listing.beds}</span>}
          {listing.baths != null && <span><span>{listing.baths}</span> {s.listing.baths}</span>}
          {listing.area != null && <span><span>{listing.area}</span> {s.listing.sqm}</span>}
        </div>
      </div>
    </article>
  );
}

/** The design's card skeleton, from home-ar-states.html. */
export function ListingCardSkeleton() {
  return (
    <div className="sq-listing-card" aria-busy="true">
      <div className="sq-skeleton" style={{ aspectRatio: "4/3", borderRadius: 0 }} />
      <div className="sq-listing-card__body">
        <div className="sq-skeleton" style={{ height: 22, width: "45%" }} />
        <div className="sq-skeleton" style={{ height: 16, width: "80%" }} />
        <div className="sq-skeleton" style={{ height: 14, width: "60%" }} />
      </div>
    </div>
  );
}

/* The heart.
 *
 * It reflects the SERVER's saved set, shared by every card on the page -- one
 * fetch for a grid of twenty, not twenty. A guest is sent to sign in with a
 * return path, because saving needs an account and a heart that silently did
 * nothing would be the worse answer.
 *
 * It sits inside the card's link, so the click has to be stopped from
 * following it.
 */
function SaveHeart({ id }: { id: string }) {
  const { s } = useV3();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (profile) ensureSavedLoaded(); }, [profile]);
  const saved = useSyncExternalStore(subscribeSaved, () => isSaved(id), () => false);

  return (
    <button
      type="button"
      className="sq-save-btn"
      aria-label={saved ? s.listing.unsave : s.listing.save}
      aria-pressed={saved}
      disabled={busy}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!profile) {
          goToLoginAndReturn(navigate, location.pathname + location.search);
          return;
        }
        setBusy(true);
        try { await toggleSaved(id); } catch { /* the store reverted it */ }
        setBusy(false);
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill={saved ? "currentColor" : "none"}
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true">
        <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
      </svg>
    </button>
  );
}
